/**
 * sessionStorage access for persisted table state (filters, search, sort, pagination, date range).
 *
 * Kept free of imports on purpose: `authUtils.ts` clears this storage on session expiry, and `authUtils.ts` is
 * itself imported by `services/api.config.ts`. Importing anything app-level here would recreate the
 * `api.config.ts` → `authUtils.ts` → … → `api.config.ts` cycle described in `pages/worker/routes.constants.ts`.
 *
 * Every access is wrapped in try/catch: storage can be unavailable (private mode, disabled cookies) or full, and
 * losing a remembered filter must never break a page.
 */

/** Identifies one table on one visit (history entry) of one screen. */
export interface TableStateScope {
  visitKey: string;
  path: string;
  tableId: string;
}

/** Router state marking a redirect that should restore the screen's last table state (edit Save, "Back to list"). */
export const RESTORE_TABLE_STATE = { restoreTableState: true } as const;

const STORAGE_VERSION = 1;
const KEY_PREFIX = "cw.table";
const VISIT_KEY_PREFIX = `${KEY_PREFIX}:`;
const LAST_KEY_PREFIX = `${KEY_PREFIX}.last:`;
const RESET_KEY = `${KEY_PREFIX}.reset`;
// Per prefix: visit entries grow with history, "last" entries with every record path (/worker/1, /worker/2, …).
const MAX_ENTRIES_PER_PREFIX = 200;
// Long enough for the form's navigate(-1) to land and the tables on that page to mount.
const RESET_TTL_MS = 10_000;

interface StoredEntry {
  v: typeof STORAGE_VERSION;
  savedAt: number;
  data: unknown;
}

interface ResetMarker {
  createdAt: number;
  visitKey: string | null;
  /** Tables that already reset, so a later remount on the same visit restores what the user did since. */
  resetTables: string[];
}

const visitStorageKey = ({ visitKey, path, tableId }: TableStateScope) => `${VISIT_KEY_PREFIX}${visitKey}:${path}:${tableId}`;
const lastStorageKey = ({ path, tableId }: TableStateScope) => `${LAST_KEY_PREFIX}${path}:${tableId}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const removeKey = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to remove.
  }
};

const readJson = (key: string): unknown => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    removeKey(key);
    return null;
  }
};

const writeJson = (key: string, value: unknown) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable: the table simply won't be remembered.
  }
};

const storageKeys = (): string[] => {
  try {
    return Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index)).filter(
      (key): key is string => key !== null,
    );
  } catch {
    return [];
  }
};

const readEntry = (key: string): unknown => {
  const entry = readJson(key);
  if (!isRecord(entry)) return null;
  if (entry.v !== STORAGE_VERSION || typeof entry.savedAt !== "number") {
    removeKey(key);
    return null;
  }
  return entry.data ?? null;
};

const pruneEntries = (prefix: string) => {
  const keys = storageKeys().filter((key) => key.startsWith(prefix));
  if (keys.length <= MAX_ENTRIES_PER_PREFIX) return;

  const savedAt = (key: string) => {
    const entry = readJson(key);
    return isRecord(entry) && typeof entry.savedAt === "number" ? entry.savedAt : 0;
  };
  keys
    .map((key) => ({ key, savedAt: savedAt(key) }))
    .sort((a, b) => a.savedAt - b.savedAt)
    .slice(0, keys.length - MAX_ENTRIES_PER_PREFIX)
    .forEach(({ key }) => removeKey(key));
};

/** The state this table had on this visit, or `null`. The caller validates the shape. */
export const readVisitTableState = (scope: TableStateScope): unknown => readEntry(visitStorageKey(scope));

/** The last state this table had on this screen, on any visit, or `null`. The caller validates the shape. */
export const readLastTableState = (scope: TableStateScope): unknown => readEntry(lastStorageKey(scope));

/** Saves the table's state for this visit and as the screen's last state. */
export const writeTableState = (scope: TableStateScope, data: unknown) => {
  const entry: StoredEntry = { v: STORAGE_VERSION, savedAt: Date.now(), data };
  writeJson(visitStorageKey(scope), entry);
  writeJson(lastStorageKey(scope), entry);
  pruneEntries(VISIT_KEY_PREFIX);
  pruneEntries(LAST_KEY_PREFIX);
};

/**
 * Call after a record is created, right before `navigate(-1)`: the tables on the page the user lands on open with
 * their defaults instead of their saved state, so the new record is not hidden by an old filter.
 */
export const markTableReset = () => {
  const marker: ResetMarker = { createdAt: Date.now(), visitKey: null, resetTables: [] };
  writeJson(RESET_KEY, marker);
};

/**
 * Whether a table mounting in `scope` should ignore its saved state because a record was just created.
 * The first table to ask claims the marker for its visit, so every table on that page resets — each only once,
 * so a later remount on that visit (Back from a row, a refresh) restores what the user has done since. A table on
 * any other visit, or a marker older than a few seconds, discards it.
 */
export const consumeTableReset = ({ visitKey, path, tableId }: TableStateScope): boolean => {
  const marker = readJson(RESET_KEY);
  if (
    !isRecord(marker) ||
    typeof marker.createdAt !== "number" ||
    !(marker.visitKey === null || typeof marker.visitKey === "string") ||
    !Array.isArray(marker.resetTables)
  ) {
    if (marker !== null) removeKey(RESET_KEY);
    return false;
  }
  if (Date.now() - marker.createdAt > RESET_TTL_MS || (marker.visitKey !== null && marker.visitKey !== visitKey)) {
    removeKey(RESET_KEY);
    return false;
  }

  const tableKey = `${path}:${tableId}`;
  if (marker.resetTables.includes(tableKey)) return false;

  const claimed: ResetMarker = {
    createdAt: marker.createdAt,
    visitKey,
    resetTables: [...marker.resetTables.filter((key): key is string => typeof key === "string"), tableKey],
  };
  writeJson(RESET_KEY, claimed);
  return true;
};

/** Removes every persisted table state. Called on logout and session expiry. */
export const clearTableState = () => {
  storageKeys()
    .filter((key) => key.startsWith(KEY_PREFIX))
    .forEach(removeKey);
};
